/**
 * ═══════════════════════════════════════════════════════════════
 * Hasımoğlu Çiftliği – Route Guard Bileşenleri
 * ───────────────────────────────────────────────────────────────
 * ProtectedRoute  : Giriş yapmamış kullanıcıları login'e yönlendirir
 * RoleRoute       : Belirli rollerin erişebileceği route'ları korur
 * ═══════════════════════════════════════════════════════════════
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, ROLES } from '../../context/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * Yükleme Spinner'ı (Auth kontrol sırasında gösterilir)
 */
function LoadingScreen() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-navy-950">
      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-forest-600 to-forest-700 flex items-center justify-center shadow-lg shadow-forest-600/20 mb-4 animate-pulse">
        <span className="text-2xl">🐄</span>
      </div>
      <Loader2 className="w-6 h-6 text-forest-500 animate-spin" />
      <p className="text-sm text-navy-400 mt-3">Yükleniyor...</p>
    </div>
  );
}

/**
 * Oturum Kontrolü Yardımcısı
 */
const hasAnySession = (currentUser, userProfile) => !!currentUser || !!userProfile;

/**
 * ProtectedRoute — Auth veya Müşteri Oturumu zorunlu
 * Giriş yapmamış kullanıcılar /login'e yönlendirilir.
 */
export function ProtectedRoute({ children }) {
  const { currentUser, userProfile, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;

  if (!hasAnySession(currentUser, userProfile)) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

/**
 * RoleRoute — Rol bazlı erişim kontrolü
 */
export function RoleRoute({ children, allowedRoles = [] }) {
  const { currentUser, userProfile, loading, error } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;

  if (error && !userProfile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-navy-950 p-4 text-center">
        <div className="w-16 h-16 rounded-2xl bg-danger-500/20 flex items-center justify-center mb-4">
          <span className="text-3xl text-danger-500">⚠</span>
        </div>
        <h2 className="text-lg font-bold text-white mb-2">Profil Yüklenemedi</h2>
        <p className="text-danger-400 text-sm mb-6 max-w-md">
          {error} (Veritabanı bağlantısı kurulamamış veya yetki sorunu olabilir.)
        </p>
        <button 
          onClick={() => window.location.href = '/login'} 
          className="px-6 py-2.5 bg-navy-800 hover:bg-navy-700 text-white rounded-xl transition-colors"
        >
          Giriş Sayfasına Dön
        </button>
      </div>
    );
  }

  if (!hasAnySession(currentUser, userProfile)) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!userProfile) return <LoadingScreen />;

  // Rol kontrolü
  if (allowedRoles.length > 0 && !allowedRoles.includes(userProfile.role)) {
    return <Navigate to="/yetkisiz" replace />;
  }

  return children;
}

/**
 * GuestRoute — Sadece Giriş yapmamışlar için (Login sayfası)
 * Zaten giriş yapmış kullanıcılar rollerine göre yönlendirilir.
 */
export function GuestRoute({ children }) {
  const { currentUser, userProfile, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  if (hasAnySession(currentUser, userProfile)) {
    const redirectPath =
      userProfile?.role === ROLES.CUSTOMER ? '/hesabim' : '/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return children;
}
